import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import { AVAILABLE_TIMEZONES, getGroupedTimezones, getDefaultTimezone, type Timezone } from '../utils/timezones';

interface NotificationSchedule {
  _id: string;
  name: string;
  time: string;
  isActive: boolean;
  timezone: string;
  lastSent: string | null;
  nextScheduled: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface IncompletePO {
  _id: string;
  PO_NO: number;
  VENDOR_NAME: string;
  UPDATER: string;
  createdAt: string;
}

interface CronStatus {
  isRunning: boolean;
  nextRun: string | null;
}

// Timezone interface is now imported from utils/timezones.ts

// Helper component for timezone selector
const TimezoneSelector: React.FC<{
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
}> = ({ value, onChange, id = 'timezone', label = 'Timezone' }) => {
  // Use the grouped timezones from our utility
  const groupedTimezones = getGroupedTimezones();

  return (
    <div className="mb-3">
      <label htmlFor={id} className="form-label">{label}</label>
      <select
        className="form-select"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      >
        {Object.entries(groupedTimezones).map(([group, groupTimezones]) => (
          <optgroup key={group} label={group.replace('/', ' - ')}>
            {groupTimezones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.displayText}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <div className="form-text">
        Choose the timezone for this notification schedule. 
        Each schedule can use its own timezone.
      </div>
    </div>
  );
};

const NotificationScheduleManagement: React.FC = () => {
  const { user, token, isLoading: authLoading } = useAuth();
  const [schedules, setSchedules] = useState<NotificationSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<NotificationSchedule | null>(null);
  const [incompletePOs, setIncompletePOs] = useState<IncompletePO[]>([]);
  const [cronStatus, setCronStatus] = useState<CronStatus | null>(null);
  const [showIncompletePreview, setShowIncompletePreview] = useState(false);
  const [triggeringNotification, setTriggeringNotification] = useState(false);
  // Using hardcoded timezones instead of API loading
  const availableTimezones = AVAILABLE_TIMEZONES;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    time: '',
    isActive: true,
    timezone: getDefaultTimezone()
  });

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/notifications`);
      setSchedules(response.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load notification schedules');
    } finally {
      setLoading(false);
    }
  };

  const loadCronStatus = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/notifications/cron/status`);
      setCronStatus(response.data.data);
    } catch (err: any) {
      console.error('Failed to load cron status:', err);
    }
  };

  const loadIncompletePOs = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/notifications/incomplete/preview`);
      setIncompletePOs(response.data.data.purchaseOrders);
    } catch (err: any) {
      console.error('Failed to load incomplete POs:', err);
    }
  };

  // Timezone loading is no longer needed - using hardcoded timezones

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${config.API_BASE_URL}/notifications`, formData);
      setShowCreateForm(false);
      setFormData({ name: '', time: '', isActive: true, timezone: getDefaultTimezone() });
      loadSchedules();
      setError('');
      alert('Notification schedule created successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create notification schedule');
    }
  };

  const handleEditSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSchedule) return;
    
    try {
      await axios.put(`${config.API_BASE_URL}/notifications/${editingSchedule._id}`, formData);
      setShowEditForm(false);
      setEditingSchedule(null);
      setFormData({ name: '', time: '', isActive: true, timezone: getDefaultTimezone() });
      loadSchedules();
      setError('');
      alert('Notification schedule updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update notification schedule');
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this notification schedule?')) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/notifications/${id}`);
      loadSchedules();
      setError('');
      alert('Notification schedule deleted successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete notification schedule');
    }
  };

  const handleEditClick = (schedule: NotificationSchedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name,
      time: schedule.time,
      isActive: schedule.isActive,
      timezone: schedule.timezone || getDefaultTimezone()
    });
    setShowEditForm(true);
  };

  const handleManualTrigger = async () => {
    try {
      setTriggeringNotification(true);
      const response = await axios.post(`${config.API_BASE_URL}/notifications/trigger`);
      alert(`Notification sent successfully! ${response.data.data.incompletePOsCount} incomplete POs found.`);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to trigger notification');
    } finally {
      setTriggeringNotification(false);
    }
  };

  const handleTestTrigger = async () => {
    try {
      setTriggeringNotification(true);
      const response = await axios.post(`${config.API_BASE_URL}/notifications/trigger/test`);
      alert(`Test successful! ${response.data.data.incompletePOsCount} incomplete POs found.\n\nThis was a test - no external notification was sent.`);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to test notification');
    } finally {
      setTriggeringNotification(false);
    }
  };

  const handleForceTrigger = async () => {
    try {
      setTriggeringNotification(true);
      const response = await axios.post(`${config.API_BASE_URL}/notifications/trigger/force`);
      alert(`Force trigger successful! ${response.data.data.incompletePOsCount} incomplete POs found.\n\nThis bypassed the time check and sent the notification immediately.`);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to force trigger notification');
    } finally {
      setTriggeringNotification(false);
    }
  };

  const restartCronJob = async () => {
    try {
      await axios.post(`${config.API_BASE_URL}/notifications/cron/restart`);
      loadCronStatus();
      alert('Cron job restarted successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restart cron job');
    }
  };

  const handleDebugCron = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/notifications/cron/debug`);
      const debugInfo = response.data.data;
      
      const debugMessage = `🔍 Cron Debug Information:
      
Current Time: ${debugInfo.currentTime}
UTC Time: ${debugInfo.utcTime}
Cron Job Initialized: ${debugInfo.cronJobInitialized ? 'Yes' : 'No'}
Cron Job Running: ${debugInfo.cronJobRunning ? 'Yes' : 'No'}
Active Schedules: ${debugInfo.activeSchedules}

This will help troubleshoot why notifications aren't being sent.`;
      
      alert(debugMessage);
      console.log('Cron debug info:', debugInfo);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get cron debug information');
    }
  };

  useEffect(() => {
    // Only load data when authentication is complete and user is authenticated
    if (!authLoading && token) {
      loadSchedules();
      loadCronStatus();
      // No need to load timezones - using hardcoded data
    }
  }, [authLoading, token]);

  useEffect(() => {
    if (showIncompletePreview) {
      loadIncompletePOs();
    }
  }, [showIncompletePreview]);

  // Show loading spinner while authentication is in progress or data is loading
  if (authLoading || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {authLoading ? 'Authenticating...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  // Show error if user is not authenticated
  if (!token || !user) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Please log in to access notification management.
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="row align-items-center">
          <div className="col-md-6">
            <h5 className="mb-0">
              <i className="bi bi-bell me-2"></i>
              Notification Schedule Management
            </h5>
          </div>
          <div className="col-md-6 text-end">
            <button
              className="btn btn-primary me-2"
              onClick={() => setShowCreateForm(true)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              New Schedule
            </button>
            <button
              className="btn btn-success me-2"
              onClick={() => setShowIncompletePreview(!showIncompletePreview)}
            >
              <i className="bi bi-eye me-1"></i>
              Preview Incomplete POs
            </button>
            <button
              className="btn btn-warning me-2"
              onClick={handleManualTrigger}
              disabled={triggeringNotification}
            >
              <i className="bi bi-bell me-1"></i>
              {triggeringNotification ? 'Sending...' : 'Send Now'}
            </button>
            <button
              className="btn btn-secondary me-2"
              onClick={handleTestTrigger}
              disabled={triggeringNotification}
            >
              <i className="bi bi-flask me-1"></i>
              Test (No Webhook)
            </button>
            <button
              className="btn btn-danger me-2"
              onClick={handleForceTrigger}
              disabled={triggeringNotification}
            >
              <i className="bi bi-lightning me-1"></i>
              Force Send
            </button>
            <button
              className="btn btn-info me-2"
              onClick={restartCronJob}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Restart Cron
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={handleDebugCron}
            >
              <i className="bi bi-bug me-1"></i>
              Debug Cron
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Cron Job Status */}
        {cronStatus && (
          <div className="alert alert-info mb-3">
            <div className="row">
              <div className="col-md-6">
                <strong>Cron Job Status:</strong> 
                <span className={`badge ms-2 ${cronStatus.isRunning ? 'bg-success' : 'bg-danger'}`}>
                  {cronStatus.isRunning ? 'Running' : 'Stopped'}
                </span>
              </div>
              <div className="col-md-6">
                <strong>Next Run:</strong> 
                <span className="ms-2">
                  {cronStatus.nextRun ? new Date(cronStatus.nextRun).toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Incomplete POs Preview */}
        {showIncompletePreview && (
          <div className="card mb-3">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-list-ul me-2"></i>
                Incomplete Purchase Orders Preview ({incompletePOs.length})
              </h6>
            </div>
            <div className="card-body">
              {incompletePOs.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>PO Number</th>
                        <th>Vendor Name</th>
                        <th>Updater</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incompletePOs.map(po => (
                        <tr key={po._id}>
                          <td>{po.PO_NO}</td>
                          <td>{po.VENDOR_NAME}</td>
                          <td>{po.UPDATER || 'N/A'}</td>
                          <td>{new Date(po.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0">No incomplete purchase orders found.</p>
              )}
            </div>
          </div>
        )}

        {/* Schedules Table */}
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Time</th>
                <th>Timezone</th>
                <th>Status</th>
                <th>Last Sent</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(schedule => (
                <tr key={schedule._id}>
                  <td>{schedule.name}</td>
                  <td>
                    <span className="badge bg-primary">{schedule.time}</span>
                  </td>
                  <td>
                    <span className="badge bg-info text-white">
                      {schedule.timezone || getDefaultTimezone()}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${schedule.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {schedule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {schedule.lastSent 
                      ? new Date(schedule.lastSent).toLocaleString() 
                      : 'Never'
                    }
                  </td>
                  <td>{schedule.createdBy}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => handleEditClick(schedule)}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteSchedule(schedule._id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {schedules.length === 0 && (
          <div className="text-center py-4">
            <i className="bi bi-bell text-muted fs-1"></i>
            <p className="text-muted mt-2">No notification schedules configured</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Create First Schedule
            </button>
          </div>
        )}
      </div>

      {/* Create Schedule Modal */}
      {showCreateForm && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Notification Schedule</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateForm(false)}
                ></button>
              </div>
              <form onSubmit={handleCreateSchedule}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">Schedule Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Morning Reminder"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="time" className="form-label">Time (24-hour format)</label>
                    <input
                      type="time"
                      className="form-control"
                      id="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                    />
                    <div className="form-text">Notifications will be sent daily at this time</div>
                  </div>
                  <TimezoneSelector
                    value={formData.timezone}
                    onChange={(timezone) => setFormData({ ...formData, timezone })}
                    id="createTimezone"
                  />
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Schedule
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Schedule Modal */}
      {showEditForm && editingSchedule && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Notification Schedule</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingSchedule(null);
                  }}
                ></button>
              </div>
              <form onSubmit={handleEditSchedule}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="editName" className="form-label">Schedule Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editName"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Morning Reminder"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editTime" className="form-label">Time (24-hour format)</label>
                    <input
                      type="time"
                      className="form-control"
                      id="editTime"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                    />
                    <div className="form-text">Notifications will be sent daily at this time</div>
                  </div>
                  <TimezoneSelector
                    value={formData.timezone}
                    onChange={(timezone) => setFormData({ ...formData, timezone })}
                    id="editTimezone"
                  />
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="editIsActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="editIsActive">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingSchedule(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update Schedule
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationScheduleManagement;
